/**
 * Common MIME types enum for file uploads and validation
 * Use these constants instead of string literals for type safety
 */
export enum MimeType {
  // Documents
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  RTF = 'application/rtf',
  ODT = 'application/vnd.oasis.opendocument.text',

  // Spreadsheets
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV = 'text/csv',
  ODS = 'application/vnd.oasis.opendocument.spreadsheet',

  // Presentations
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ODP = 'application/vnd.oasis.opendocument.presentation',

  // Images - Raster
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PNG = 'image/png',
  GIF = 'image/gif',
  BMP = 'image/bmp',
  WEBP = 'image/webp',
  TIFF = 'image/tiff',
  ICO = 'image/x-icon',

  // Images - Vector
  SVG = 'image/svg+xml',

  // Text
  TXT = 'text/plain',
  HTML = 'text/html',
  CSS = 'text/css',
  JS = 'text/javascript',
  JSON = 'application/json',
  XML = 'application/xml',

  // Archives
  ZIP = 'application/zip',
  RAR = 'application/x-rar-compressed',
  TAR = 'application/x-tar',
  GZIP = 'application/gzip',
  SEVEN_ZIP = 'application/x-7z-compressed',

  // Audio
  MP3 = 'audio/mpeg',
  WAV = 'audio/wav',
  OGG = 'audio/ogg',
  M4A = 'audio/mp4',

  // Video
  MP4 = 'video/mp4',
  AVI = 'video/x-msvideo',
  MOV = 'video/quicktime',
  WMV = 'video/x-ms-wmv',
  WEBM = 'video/webm',
}

/**
 * Predefined arrays of MIME types for common use cases
 */
export const FileTypeGroups = {
  DOCUMENTS: [
    MimeType.PDF,
    MimeType.DOC,
    MimeType.DOCX,
    MimeType.RTF,
    MimeType.ODT,
  ] as const,

  SPREADSHEETS: [
    MimeType.XLS,
    MimeType.XLSX,
    MimeType.CSV,
    MimeType.ODS,
  ] as const,

  PRESENTATIONS: [MimeType.PPT, MimeType.PPTX, MimeType.ODP] as const,

  IMAGES: [
    MimeType.JPEG,
    MimeType.JPG,
    MimeType.PNG,
    MimeType.GIF,
    MimeType.BMP,
    MimeType.WEBP,
    MimeType.SVG,
  ] as const,

  RASTER_IMAGES: [
    MimeType.JPEG,
    MimeType.JPG,
    MimeType.PNG,
    MimeType.GIF,
    MimeType.BMP,
    MimeType.WEBP,
  ] as const,

  VECTOR_IMAGES: [MimeType.SVG] as const,

  TEXT_FILES: [
    MimeType.TXT,
    MimeType.HTML,
    MimeType.CSS,
    MimeType.JS,
    MimeType.JSON,
    MimeType.XML,
  ] as const,

  ARCHIVES: [
    MimeType.ZIP,
    MimeType.RAR,
    MimeType.TAR,
    MimeType.GZIP,
    MimeType.SEVEN_ZIP,
  ] as const,

  AUDIO: [MimeType.MP3, MimeType.WAV, MimeType.OGG, MimeType.M4A] as const,

  VIDEO: [
    MimeType.MP4,
    MimeType.AVI,
    MimeType.MOV,
    MimeType.WMV,
    MimeType.WEBM,
  ] as const,

  // Common combinations
  PDF_ONLY: [MimeType.PDF] as const,

  PDF_AND_IMAGES: [
    MimeType.PDF,
    MimeType.JPEG,
    MimeType.JPG,
    MimeType.PNG,
    MimeType.BMP,
  ] as const,

  OFFICE_DOCUMENTS: [
    MimeType.PDF,
    MimeType.DOC,
    MimeType.DOCX,
    MimeType.XLS,
    MimeType.XLSX,
    MimeType.PPT,
    MimeType.PPTX,
  ] as const,

  ALL_DOCUMENTS: [
    MimeType.PDF,
    MimeType.DOC,
    MimeType.DOCX,
    MimeType.RTF,
    MimeType.ODT,
    MimeType.XLS,
    MimeType.XLSX,
    MimeType.CSV,
    MimeType.PPT,
    MimeType.PPTX,
  ] as const,
}
